import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

export default function PageTransition({ children }) {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const [showContent, setShowContent] = useState(false);
  const timerRef = useRef(null);

  // Synchronously detect path change during render (before useEffect)
  const isNewPath = location.pathname !== previousPathRef.current;

  // If path changed, we need to show skeleton
  // This happens synchronously during render, before paint
  if (isNewPath) {
    previousPathRef.current = location.pathname;
    // We can't call setState during render, but we can use this flag
  }

  // Compute what to show: skeleton if new path or not yet ready
  const shouldShowSkeleton = isNewPath || !showContent;

  useEffect(() => {
    // Reset and start timer on path change
    setShowContent(false);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setShowContent(true);
    }, 350);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location.pathname]);

  return (
    <div className="relative">
      {/* White overlay - shown during transition */}
      <AnimatePresence>
        {shouldShowSkeleton && (
          <motion.div
            key={`overlay-${location.pathname}`}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-10 bg-white"
          />
        )}
      </AnimatePresence>

      {/* Children always render (so data loading starts immediately) */}
      {/* They're just hidden behind the skeleton during transition */}
      <motion.div
        key={`content-${location.pathname}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: shouldShowSkeleton ? 0 : 1 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}
